/**
 * RhDossiersAgentsPanel — le dossier agent, socle des RH (mig 230/231).
 *
 * ─── CE QUE CET ÉCRAN SERT À FAIRE ──────────────────────────────────────────
 *
 * C'est la table dont tout le reste découle : habilitations, visites médicales,
 * échelons, organigramme, contrats, CNAS, SFT, annuaire. La RH tient son
 * fichier ici, ou l'importe d'un coup depuis Excel.
 *
 * L'écran ouvre donc sur la LISTE (qui manque, qui est incomplet), pas sur un
 * formulaire : le geste courant est de vérifier l'état du parc d'agents.
 *
 * ─── 🔒 LE NIR ─────────────────────────────────────────────────────────────
 *
 * Il se saisit mais ne se relit jamais : l'API ne renvoie qu'un booléen. Le
 * champ affiche donc « renseigné » plutôt qu'une valeur, et le laisser vide à
 * la modification ne l'efface pas — il faut le vider explicitement.
 *
 * ─── ALLER-RETOUR EXCEL ─────────────────────────────────────────────────────
 *
 * Export et modèle partagent les mêmes colonnes : on exporte, on corrige en
 * masse dans Excel, on réimporte. Le NIR n'y figure pas — un fichier circule
 * par mail et traîne sur les postes.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Alert, Button } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { RhAgentDossier, RhReferentiels } from '@roronoa-collectivites/shared-types';
import { Users, Plus, Download, Upload, FileSpreadsheet, Trash2, Pencil, Info, ShieldCheck, TrendingUp, ClipboardList } from 'lucide-react';
import { RhCarrierePanel } from './RhCarrierePanel';
import { SuivisAgentDialog } from '@/components/rh/SuivisAgentDialog';

/**
 * Libellés d'AFFICHAGE seulement (colonne « Statut » du tableau).
 *
 * Le formulaire, lui, peuple ses listes depuis `api.rh.referentielsRh()` : la
 * source de vérité est le serveur. Ces constantes ne servent qu'à traduire une
 * valeur déjà enregistrée — elles ne doivent jamais servir à proposer un choix,
 * sous peine de diverger du back.
 */
const STATUTS = [
  { value: '', label: '—' },
  { value: 'titulaire', label: 'Titulaire' },
  { value: 'stagiaire', label: 'Stagiaire' },
  { value: 'contractuel', label: 'Contractuel' },
  { value: 'vacataire', label: 'Vacataire' },
];
const CONTRATS = [
  { value: '', label: '—' },
  { value: 'cdi', label: 'CDI' },
  { value: 'cdd', label: 'CDD' },
  { value: 'stage', label: 'Stage' },
  { value: 'apprentissage', label: 'Apprentissage' },
];

const fr = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

type Draft = Partial<RhAgentDossier> & { nom: string; prenom: string; nir?: string };
const VIDE: Draft = { nom: '', prenom: '', langues: [] };

export function RhDossiersAgentsPanel() {
  // Mig 237 — la carrière s'ouvre en plein écran plutôt qu'en modale : trois
  // blocs et deux formulaires ne tiennent pas dans une boîte de dialogue.
  const [carriere, setCarriere] = useState<{ id: string; nom: string } | null>(null);
  // M22 — suivis (raison, objectif, circuit, date butoir) sur le dossier.
  const [suivis, setSuivis] = useState<{ id: string; nom: string } | null>(null);
  const { confirm, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<RhAgentDossier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [recherche, setRecherche] = useState('');
  // Référentiels de la commune : grades et cadres d'emploi nommés une fois,
  // proposés en liste plutôt que saisis à la main (autant d'orthographes que de
  // saisies, sinon — et l'organigramme devient inexploitable).
  const [ref, setRef] = useState<RhReferentiels | null>(null);

  /**
   * L'import Excel : le point de départ d'une commune. Dossiers créés en
   * masse, comptes existants rattachés, invitations envoyées d'un coup.
   */
  async function importerFichier(f: File) {
    setMsg(null); setError(null);
    try {
      const r = await api.rh.importerAgentsExcel(f);
      let m = `Import terminé : ${r.crees} dossier${r.crees > 1 ? 's' : ''} créé${r.crees > 1 ? 's' : ''}`
        + `${r.majs ? `, ${r.majs} mis à jour` : ''}`;
      if (r.invitationsEnvoyees) {
        m += ` · ${r.invitationsEnvoyees} invitation${r.invitationsEnvoyees > 1 ? 's' : ''} envoyée${r.invitationsEnvoyees > 1 ? 's' : ''}`;
      }
      if (r.habilitations) m += ` · ${r.habilitations} habilitation${r.habilitations > 1 ? 's' : ''}`;
      if (r.invitationsErreurs.length) {
        m += `. Invitations à envoyer depuis l'onglet Invitations : ${r.invitationsErreurs.map((x) => x.email).join(', ')}`;
      }
      if (r.erreurs.length) {
        m += `. ${r.erreurs.length} ligne${r.erreurs.length > 1 ? 's' : ''} en erreur : `
          + r.erreurs.slice(0, 3).map((x) => `L${x.ligne} (${x.message})`).join(' · ')
          + (r.erreurs.length > 3 ? '…' : '');
      }
      setMsg(m);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Import impossible');
    }
  }

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [liste, referentiels] = await Promise.all([
        api.rh.dossiersAgents(),
        api.rh.referentielsRh(),
      ]);
      setItems(liste); setRef(referentiels);
    }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function enregistrer() {
    if (!draft || !draft.nom.trim() || !draft.prenom.trim()) {
      setError('Nom et prénom sont obligatoires'); return;
    }
    setSaving(true); setError(null);
    try {
      const body = { ...draft, langues: draft.langues ?? [] };
      if (draft.id) await api.rh.modifierDossierAgent(draft.id, body);
      else await api.rh.creerDossierAgent(body);
      setMsg(draft.id ? 'Dossier mis à jour.' : 'Dossier créé.');
      setDraft(null);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setSaving(false); }
  }

  async function desactiver(a: RhAgentDossier) {
    const ok = await confirm({
      title: 'Désactiver ce dossier ?',
      description: `${a.prenom} ${a.nom} n'apparaîtra plus dans la liste. Le dossier et son historique de carrière sont conservés.`,
      confirmLabel: 'Désactiver', danger: true,
    });
    if (!ok) return;
    try { await api.rh.supprimerDossierAgent(a.id); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  const filtres = recherche.trim().length > 0
    ? items.filter((a) => `${a.nom} ${a.prenom} ${a.matricule ?? ''}`.toLowerCase()
        .includes(recherche.trim().toLowerCase()))
    : items;

  const champ = 'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink';
  const label = 'mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle';
  const set = (k: keyof Draft, v: unknown) => setDraft((d) => (d ? { ...d, [k]: v } : d));

  // La carrière prend toute la place quand elle est ouverte : on ne superpose
  // pas deux écrans denses.
  if (carriere) {
    return (
      <RhCarrierePanel
        agentId={carriere.id}
        agentNom={carriere.nom}
        onRetour={() => { setCarriere(null); void charger(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {dialogElement}
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card variant="glass" padded>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <Users className="h-4 w-4 text-rh-fg" aria-hidden="true" />
            Dossiers agents
            <span className="text-xs font-normal text-ink-muted">({items.length})</span>
          </h3>
          <input
            value={recherche} onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher…" aria-label="Rechercher un agent"
            className="ml-auto h-8 w-48 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink"
          />
          <Button variant="ghost" size="sm" onClick={() => void api.rh.telechargerModeleAgents()}>
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" /> Modèle
          </Button>
          <label
            htmlFor="import-agents"
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-r-md border border-border px-2.5 py-1.5 text-xs font-medium text-ink-muted hover:border-rh/40 hover:text-ink"
          >
            <Upload className="h-4 w-4" aria-hidden="true" /> Importer
          </label>
          <input id="import-agents" type="file" className="sr-only"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importerFichier(f);
              e.target.value = '';
            }} />
          <Button variant="secondary" size="sm" onClick={() => void api.rh.telechargerAgentsExcel()}>
            <Download className="h-4 w-4" aria-hidden="true" /> Exporter
          </Button>
          <Button variant="primary" size="sm" onClick={() => { setDraft({ ...VIDE }); setMsg(null); }}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter
          </Button>
        </div>

        <p className="mb-3 flex items-start gap-1.5 text-xs text-ink-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Cette table alimente les autres écrans RH : habilitations, suivi médical,
            échelons, organigramme, annuaire. Le numéro de sécurité sociale est chiffré
            et <strong>n'apparaît jamais dans les exports</strong>.
          </span>
        </p>

        {loading ? (
          <p className="text-sm text-ink-subtle">Chargement…</p>
        ) : filtres.length === 0 ? (
          <p className="text-sm text-ink-subtle">
            {items.length === 0
              ? 'Aucun dossier. Créez-en un ou importez votre fichier Excel.'
              : 'Aucun résultat pour cette recherche.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                  <th className="py-2 font-medium">Agent</th>
                  <th className="py-2 font-medium">Matricule</th>
                  <th className="py-2 font-medium">Statut</th>
                  <th className="py-2 font-medium">Grade · échelon</th>
                  <th className="py-2 font-medium">Entrée commune</th>
                  <th className="py-2 font-medium">Compte</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {filtres.map((a) => (
                  <tr key={a.id} className="border-b border-border/60 last:border-0">
                    <td className="py-2 text-ink">
                      {a.prenom} {a.nom}
                      {a.dateFinContrat && (
                        <span className="ml-1.5 text-2xs uppercase tracking-r-eyebrow text-warning">
                          fin {fr(a.dateFinContrat)}
                        </span>
                      )}
                    </td>
                    <td className="py-2 tabular-nums text-ink-muted">{a.matricule ?? '—'}</td>
                    <td className="py-2 text-ink-muted">
                      {STATUTS.find((s) => s.value === a.statut)?.label ?? '—'}
                      {a.typeContrat && ` · ${CONTRATS.find((c) => c.value === a.typeContrat)?.label}`}
                    </td>
                    <td className="py-2 text-ink-muted">
                      {a.grade ?? '—'}{a.echelon ? ` · ${a.echelon}` : ''}
                    </td>
                    <td className="py-2 tabular-nums text-ink-muted">{fr(a.dateEntreeCommune)}</td>
                    <td className="py-2">
                      {a.userId
                        ? <Badge variant="success">Actif</Badge>
                        : a.emailInvitation
                          ? <Badge variant="warning">Invitation en attente</Badge>
                          : <Badge variant="neutral">Sans compte</Badge>}
                    </td>
                    <td className="py-2 text-right">
                      <Button variant="ghost" size="sm"
                        onClick={() => setCarriere({ id: a.id, nom: `${a.prenom} ${a.nom}` })}
                        title="Carrière : grades, échelons, contrats">
                        <TrendingUp className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button variant="ghost" size="sm"
                        onClick={() => setSuivis({ id: a.id, nom: `${a.prenom} ${a.nom}` })}
                        title="Suivis : médical, dossier, entretien… avec circuit de décision"
                        aria-label={`Suivis de ${a.prenom} ${a.nom}`}>
                        <ClipboardList className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setDraft({ ...a, nir: undefined }); setMsg(null); }}
                        aria-label={`Modifier le dossier de ${a.prenom} ${a.nom}`}>
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => void desactiver(a)}
                        aria-label={`Désactiver le dossier de ${a.prenom} ${a.nom}`}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {draft && (
        <Card variant="glass" padded>
          <p className="mb-3 text-sm font-semibold text-ink">
            {draft.id ? `Dossier — ${draft.prenom} ${draft.nom}` : 'Nouveau dossier agent'}
          </p>

          <div className="space-y-4">
            <section>
              <p className="mb-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">État civil</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <div><label className={label}>Prénom *</label>
                  <input value={draft.prenom} onChange={(e) => set('prenom', e.target.value)} className={champ} /></div>
                <div><label className={label}>Nom *</label>
                  <input value={draft.nom} onChange={(e) => set('nom', e.target.value)} className={champ} /></div>
                <div><label className={label}>Nom de naissance</label>
                  <input value={draft.nomNaissance ?? ''} onChange={(e) => set('nomNaissance', e.target.value)} className={champ} /></div>
                <div><label className={label}>Date de naissance</label>
                  <input type="date" value={draft.dateNaissance ?? ''} onChange={(e) => set('dateNaissance', e.target.value)} className={champ} /></div>
                <div><label className={label}>Lieu de naissance</label>
                  <input value={draft.lieuNaissance ?? ''} onChange={(e) => set('lieuNaissance', e.target.value)} className={champ} /></div>
                <div><label className={label}>Matricule</label>
                  <input value={draft.matricule ?? ''} onChange={(e) => set('matricule', e.target.value)}
                    placeholder="Attribué automatiquement" className={champ} />
                  {/* Laisser vide suffit : le serveur donne le numéro suivant de
                      la commune. Une valeur saisie n'est jamais écrasée — c'est
                      ce qui permet de garder la numérotation du logiciel de paie. */}
                  <p className="mt-1 text-2xs text-ink-subtle">
                    Laissez vide : le numéro suivant de la commune est attribué à
                    l'enregistrement. Une valeur saisie est conservée telle quelle.
                  </p></div>
              </div>
            </section>

            <section>
              <p className="mb-2 flex items-center gap-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                Numéro de sécurité sociale
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <input
                    value={draft.nir ?? ''} onChange={(e) => set('nir', e.target.value)}
                    className={champ} maxLength={20}
                    placeholder={draft.nirRenseigne ? '•••••••••••••  (renseigné)' : 'Non renseigné'}
                    aria-label="Numéro de sécurité sociale"
                  />
                </div>
                <p className="self-center text-xs text-ink-subtle">
                  Chiffré en base, jamais réaffiché ni exporté. Laisser vide ne l'efface pas.
                </p>
              </div>
            </section>

            <section>
              <p className="mb-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Carrière</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <div><label className={label}>Entrée dans la commune</label>
                  <input type="date" value={draft.dateEntreeCommune ?? ''} onChange={(e) => set('dateEntreeCommune', e.target.value)} className={champ} /></div>
                <div><label className={label}>Entrée fonction publique</label>
                  <input type="date" value={draft.dateEntreeFonctionPublique ?? ''} onChange={(e) => set('dateEntreeFonctionPublique', e.target.value)} className={champ} /></div>
                <div><label className={label}>Statut</label>
                  <select value={draft.statut ?? ''} onChange={(e) => set('statut', e.target.value || null)} className={champ}>
                    <option value="">—</option>
                    {(ref?.statuts ?? []).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select></div>
                <div><label className={label}>Type de contrat</label>
                  <select value={draft.typeContrat ?? ''} onChange={(e) => set('typeContrat', e.target.value || null)} className={champ}>
                    <option value="">—</option>
                    {(ref?.typesContrat ?? []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select></div>
                <div><label className={label}>Fin de contrat</label>
                  <input type="date" value={draft.dateFinContrat ?? ''} onChange={(e) => set('dateFinContrat', e.target.value)} className={champ} /></div>
                <div><label className={label}>Quotité (%)</label>
                  <input type="number" min={1} max={100} value={draft.quotiteTravail ?? 100}
                    onChange={(e) => set('quotiteTravail', Number(e.target.value))} className={champ} /></div>
                <div><label className={label}>Cadre d'emploi</label>
                  <select value={draft.cadreEmploi ?? ''} onChange={(e) => set('cadreEmploi', e.target.value || null)} className={champ}>
                    <option value="">—</option>
                    {(ref?.cadresEmploi ?? []).map((c) => (
                      <option key={c.id} value={c.libelle}>{c.libelle}</option>
                    ))}
                  </select></div>
                <div><label className={label}>Grade</label>
                  <select value={draft.grade ?? ''} onChange={(e) => set('grade', e.target.value || null)} className={champ}>
                    <option value="">—</option>
                    {(ref?.grades ?? []).map((g) => (
                      <option key={g.id} value={g.libelle}>{g.libelle}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-ink-subtle">
                    Manque un grade ? Ajoutez-le dans les réglages RH : il sera proposé partout.
                  </p></div>
                {/* Échelon : liste BORNÉE par le grade choisi, du plus petit au
                    plus grand (28/07). Un champ nombre libre laissait saisir
                    « 14 » sur un grade qui en compte 11. Si le grade n'annonce
                    pas ses échelons, on retombe sur 20 — la borne du CHECK. */}
                <div><label className={label}>Échelon</label>
                  <select value={draft.echelon ?? ''}
                    onChange={(e) => set('echelon', e.target.value ? Number(e.target.value) : null)}
                    className={champ}>
                    <option value="">—</option>
                    {Array.from(
                      { length: (ref?.grades ?? []).find((g) => g.libelle === draft.grade)?.echelonsMax ?? 20 },
                      (_, i) => i + 1,
                    ).map((n) => <option key={n} value={n}>{n}</option>)}
                  </select></div>
                <div><label className={label}>Indice brut</label>
                  <input type="number" min={0} max={2000} value={draft.indiceBrut ?? ''}
                    onChange={(e) => set('indiceBrut', e.target.value ? Number(e.target.value) : null)}
                    className={champ} />
                  <p className="mt-1 text-xs text-ink-subtle">
                    Classement indiciaire — distinct de l'indice majoré, qui sert
                    au calcul du traitement.
                  </p></div>
                {/* Catégorie : fermée, elle relève du statut. */}
                <div><label className={label}>Catégorie hiérarchique</label>
                  <select value={draft.categorieHierarchique ?? ''}
                    onChange={(e) => set('categorieHierarchique', e.target.value || null)}
                    className={champ}>
                    <option value="">—</option>
                    {['A', 'B', 'C'].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                {/* Service au sens de l'ORGANIGRAMME — pas la brique applicative.
                    C'est ce libellé qui part sur les arrêtés. */}
                <div><label className={label}>Service</label>
                  <select value={draft.serviceId ?? ''}
                    onChange={(e) => set('serviceId', e.target.value || null)}
                    className={champ}>
                    <option value="">—</option>
                    {(ref?.services ?? []).map((sv) => (
                      <option key={sv.id} value={sv.id}>
                        {sv.libelleOfficiel ?? sv.libelle}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-ink-subtle">
                    Les services se créent dans les réglages RH — on saisit une
                    fois, on choisit ensuite.
                  </p></div>
                <div><label className={label}>Fonction (organigramme)</label>
                  <input value={draft.fonction ?? ''} onChange={(e) => set('fonction', e.target.value)} className={champ} /></div>
              </div>
            </section>

            <section>
              <p className="mb-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Coordonnées & compte</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <div><label className={label}>E-mail du compte VPC</label>
                  <input type="email" value={draft.emailInvitation ?? draft.emailCompte ?? ''}
                    onChange={(e) => set('emailInvitation', e.target.value)} className={champ}
                    disabled={Boolean(draft.userId)} />
                  <p className="mt-1 text-xs text-ink-subtle">
                    {draft.userId ? 'Compte déjà actif.' : "Sert au rattachement à l'activation."}
                  </p></div>
                <div><label className={label}>E-mail personnel</label>
                  <input type="email" value={draft.emailPerso ?? ''} onChange={(e) => set('emailPerso', e.target.value)} className={champ} /></div>
                <div><label className={label}>Téléphone personnel</label>
                  <input value={draft.telephonePerso ?? ''} onChange={(e) => set('telephonePerso', e.target.value)} className={champ} /></div>
                <div className="sm:col-span-2"><label className={label}>Adresse</label>
                  <input value={draft.adresse ?? ''} onChange={(e) => set('adresse', e.target.value)} className={champ} /></div>
                <div><label className={label}>Ville</label>
                  <input value={draft.ville ?? ''} onChange={(e) => set('ville', e.target.value)} className={champ} /></div>
                <div><label className={label}>Contact d'urgence — nom</label>
                  <input value={draft.urgenceNom ?? ''} onChange={(e) => set('urgenceNom', e.target.value)} className={champ} /></div>
                <div><label className={label}>Contact d'urgence — téléphone</label>
                  <input value={draft.urgenceTel ?? ''} onChange={(e) => set('urgenceTel', e.target.value)} className={champ} /></div>
                <div><label className={label}>Lien</label>
                  <input value={draft.urgenceLien ?? ''} onChange={(e) => set('urgenceLien', e.target.value)} className={champ} placeholder="conjoint, fille…" /></div>
              </div>
            </section>

            <section>
              <p className="mb-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Compléments</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <div><label className={label}>Langues (séparées par ;)</label>
                  <input value={(draft.langues ?? []).join(';')}
                    onChange={(e) => set('langues', e.target.value.split(';').map((x) => x.trim()).filter(Boolean))}
                    className={champ} placeholder="anglais;langue des signes" /></div>
                <div>
                  <label className={label}>Nombre d'enfants (SFT)</label>
                  <input type="number" min={0} max={20} value={draft.nombreEnfants ?? ''}
                    onChange={(e) => set('nombreEnfants', e.target.value ? Number(e.target.value) : null)} className={champ} />
                  <label className="mt-1.5 flex items-center gap-2 text-sm text-ink">
                    <input type="checkbox" checked={draft.sftActif ?? false}
                      onChange={(e) => set('sftActif', e.target.checked)} className="h-4 w-4 rounded border-border" />
                    Droit au SFT ouvert
                  </label>
                  {draft.sftActif && (
                    <>
                      <input type="date" value={draft.sftDateDebut ?? ''}
                        onChange={(e) => set('sftDateDebut', e.target.value || null)}
                        className={`${champ} mt-1.5`} aria-label="Début du droit SFT" />
                      <input type="date" value={draft.sftDateFin ?? ''}
                        onChange={(e) => set('sftDateFin', e.target.value || null)}
                        className={`${champ} mt-1.5`} aria-label="Fin prévue du droit SFT" />
                      <p className="mt-1 text-2xs text-ink-subtle">Début · fin prévue</p>
                    </>
                  )}
                </div>
                <div><label className={label}>Secteur CNAS</label>
                  <input value={draft.cnasSecteur ?? ''} onChange={(e) => set('cnasSecteur', e.target.value)} className={champ} /></div>
                {/*
                  Le numéro d'adhérent n'apparaît QUE si la case est cochée :
                  demander un numéro à quelqu'un qui n'adhère pas encombre le
                  formulaire pour rien.
                */}
                <div>
                  <label className="flex items-center gap-2 text-sm text-ink">
                    <input type="checkbox" checked={draft.cnasAdherent ?? false}
                      onChange={(e) => set('cnasAdherent', e.target.checked)} className="h-4 w-4 rounded border-border" />
                    Adhérent CNAS
                  </label>
                  {draft.cnasAdherent && (
                    <>
                      <input value={draft.cnasNumeroAdherent ?? ''}
                        onChange={(e) => set('cnasNumeroAdherent', e.target.value)}
                        className={`${champ} mt-1.5`} placeholder="N° d'adhérent" maxLength={60} />
                      {/*
                        L'échéance était en base et dans le fichier Excel, mais
                        n'existait que sur l'écran Carrière : la RH qui saisit un
                        dossier ne la trouvait pas. C'est ELLE qui déclenche le
                        rappel de renouvellement.
                      */}
                      <input type="date" value={draft.cnasEcheance ?? ''}
                        onChange={(e) => set('cnasEcheance', e.target.value || null)}
                        className={`${champ} mt-1.5`} aria-label="Échéance CNAS" />
                      <p className="mt-1 text-2xs text-ink-subtle">Échéance d'adhésion</p>
                    </>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-ink">
                    <input type="checkbox" checked={draft.pscPrevoyance ?? false}
                      onChange={(e) => set('pscPrevoyance', e.target.checked)} className="h-4 w-4 rounded border-border" />
                    PSC prévoyance
                  </label>
                  {draft.pscPrevoyance && (
                    <>
                      <input value={draft.pscPrevoyanceNumero ?? ''}
                        onChange={(e) => set('pscPrevoyanceNumero', e.target.value)}
                        className={`${champ} mt-1.5`} placeholder="N° adhérent" maxLength={60} />
                      <input type="date" value={draft.pscPrevoyanceEcheance ?? ''}
                        onChange={(e) => set('pscPrevoyanceEcheance', e.target.value || null)}
                        className={`${champ} mt-1.5`} aria-label="Échéance prévoyance" />
                      <p className="mt-1 text-2xs text-ink-subtle">Échéance du contrat</p>
                    </>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-ink">
                    <input type="checkbox" checked={draft.pscSante ?? false}
                      onChange={(e) => set('pscSante', e.target.checked)} className="h-4 w-4 rounded border-border" />
                    PSC santé
                  </label>
                  {draft.pscSante && (
                    <>
                      <input value={draft.pscSanteNumero ?? ''}
                        onChange={(e) => set('pscSanteNumero', e.target.value)}
                        className={`${champ} mt-1.5`} placeholder="N° adhérent" maxLength={60} />
                      {/* Deux échéances distinctes : la santé et la prévoyance
                          sont deux contrats, avec deux organismes. */}
                      <input type="date" value={draft.pscSanteEcheance ?? ''}
                        onChange={(e) => set('pscSanteEcheance', e.target.value || null)}
                        className={`${champ} mt-1.5`} aria-label="Échéance santé" />
                      <p className="mt-1 text-2xs text-ink-subtle">Échéance du contrat</p>
                    </>
                  )}
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>Annuler</Button>
              <Button variant="primary" size="sm" loading={saving} onClick={() => void enregistrer()}>
                {draft.id ? 'Enregistrer' : 'Créer le dossier'}
              </Button>
            </div>
          </div>
        </Card>
      )}
      {/* M22 — la fenêtre des suivis de l'agent sélectionné. */}
      {suivis && (
        <SuivisAgentDialog agentId={suivis.id} agentNom={suivis.nom} onClose={() => setSuivis(null)} />
      )}
    </div>
  );
}
